import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Chip, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import axios from "axios";
import { MdOutlineWorkspacePremium } from "react-icons/md";
import { useHistory } from "react-router-dom";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { errorAlert, getApiErrorMessage, handleSuccess } from "../../Components/Alerts";
import CrudPageHeader from "../../Components/CrudPageHeader";
import ReusableServerDataGrid from "../../Components/ReusableServerDataGrid";

function formatDate(value) {
  if (!value) {
    return "-";
  }

  const raw = String(value);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return raw;
  }

  const [, year, month, day] = match;
  return `${day}.${month}.${year}`;
}

function toDateInputValue(value) {
  if (!value) {
    return "";
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getStatusChipProps(status) {
  const normalized = String(status || "").trim().toLowerCase();

  if (normalized === "pending") {
    return {
      label: "Pending",
      color: "warning",
      sx: { borderRadius: "999px", fontWeight: 700 },
    };
  }

  if (normalized === "approved") {
    return {
      label: "Approved",
      color: "success",
      sx: { borderRadius: "999px", fontWeight: 700 },
    };
  }

  if (normalized === "rejected") {
    return {
      label: "Rejected",
      color: "error",
      sx: { borderRadius: "999px", fontWeight: 700 },
    };
  }

  if (normalized === "cancelled") {
    return {
      label: "Cancelled",
      color: "default",
      sx: { borderRadius: "999px", fontWeight: 700 },
    };
  }

  return {
    label: status || "-",
    color: "default",
    sx: { borderRadius: "999px", fontWeight: 700 },
  };
}

function readFirstDefinedValue(source, keys) {
  if (!source) {
    return "";
  }

  const normalizedKeys = new Set(
    Object.keys(source).map((key) => String(key).trim().toLowerCase())
  );

  for (const key of keys) {
    const normalizedKey = String(key).trim().toLowerCase();
    const value = source[key] ?? source[Object.keys(source).find((candidate) => String(candidate).trim().toLowerCase() === normalizedKey)];

    if (value === 0 || value === false) {
      return value;
    }

    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return "";
}

function normalizeRenewalRow(item) {
  const source = item?.rowInfo || item || {};

  return {
    entitlementId: readFirstDefinedValue(source, ["entitlementId", "EntitlementId", "EntitlementID"]),
    companyId: readFirstDefinedValue(source, ["companyId", "CompanyId", "CompanyID"]),
    companyName: readFirstDefinedValue(source, ["companyName", "CompanyName"]),
    productCode: readFirstDefinedValue(source, ["productCode", "ProductCode"]),
    sourceLicenseId: readFirstDefinedValue(source, ["sourceLicenseId", "SourceLicenseId", "SourceLicenseID"]),
    startDate: readFirstDefinedValue(source, ["startDate", "StartDate"]),
    endDate: readFirstDefinedValue(source, ["endDate", "EndDate"]),
    isActive: readFirstDefinedValue(source, ["isActive", "IsActive"]),
    fiscalCashRegisterId: readFirstDefinedValue(source, ["fiscalCashRegisterId", "FiscalCashRegisterId", "FiscalCashRegisterID"]),
    machineHash: readFirstDefinedValue(source, ["machineHash", "MachineHash"]),
    renewalStatus: readFirstDefinedValue(source, ["renewalStatus", "RenewalStatus"]),
    renewalRequestedAt: readFirstDefinedValue(source, ["renewalRequestedAt", "RenewalRequestedAt"]),
    renewalRequestedAppVersion: readFirstDefinedValue(source, ["renewalRequestedAppVersion", "RenewalRequestedAppVersion"]),
    renewalRequestedDeviceName: readFirstDefinedValue(source, ["renewalRequestedDeviceName", "RenewalRequestedDeviceName"]),
    renewalNote: readFirstDefinedValue(source, ["renewalNote", "RenewalNote"]),
    fiscalCashRegisterName: readFirstDefinedValue(source, ["fiscalCashRegisterName", "FiscalCashRegisterName"]),
  };
}

export default function BarTreeRenewalsPage() {
  const headers = authHeader();
  const history = useHistory();

  const [rows, setRows] = useState([]);
  const [activeLicenses, setActiveLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeLicensesLoading, setActiveLicensesLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeLicensesError, setActiveLicensesError] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = useState([{ field: "renewalRequestedAt", sort: "asc" }]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [quickFilter, setQuickFilter] = useState([]);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [selectedRenewal, setSelectedRenewal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [rejectError, setRejectError] = useState("");
  const [approveStartDate, setApproveStartDate] = useState("");
  const [approveEndDate, setApproveEndDate] = useState("");
  const [approveNote, setApproveNote] = useState("");
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState("");

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.get(ApiLink.API_URL + "License/bar-tree-renewals", {
        headers,
      });

      const items = Array.isArray(response?.data) ? response.data : [];
      setRows(items.map(normalizeRenewalRow));
    } catch (err) {
      setRows([]);
      setError("Gabim gjatë ngarkimit të kërkesave të rilicencimit për SEF.");
      errorAlert("Gabim gjatë ngarkimit të kërkesave të rilicencimit për SEF.");
    } finally {
      setLoading(false);
    }
  };

  const loadActiveLicenses = async () => {
    try {
      setActiveLicensesLoading(true);
      setActiveLicensesError("");

      const response = await axios.get(ApiLink.API_URL + "License/bar-tree-active-licenses", {
        headers,
      });

      const items = Array.isArray(response?.data) ? response.data : [];
      setActiveLicenses(items.map((item) => {
        const source = item?.rowInfo || item || {};
        const companyName = readFirstDefinedValue(source, ["companyName", "CompanyName"]);
        const productCode = readFirstDefinedValue(source, ["productCode", "ProductCode"]);
        const fiscalCashRegisterName = readFirstDefinedValue(source, ["fiscalCashRegisterName", "FiscalCashRegisterName"]);
        const startDate = readFirstDefinedValue(source, ["startDate", "StartDate"]);
        const endDate = readFirstDefinedValue(source, ["endDate", "EndDate"]);
        const isActive = readFirstDefinedValue(source, ["isActive", "IsActive"]);
        const machineHash = readFirstDefinedValue(source, ["machineHash", "MachineHash"]);
        const fiscalCashRegisterId = readFirstDefinedValue(source, ["fiscalCashRegisterId", "FiscalCashRegisterId", "FiscalCashRegisterID"]);

        return {
          ...item,
          companyName,
          productCode,
          fiscalCashRegisterName,
          startDate,
          endDate,
          isActive,
          machineHash,
          fiscalCashRegisterId,
          id: item?.entitlementId ?? item?.EntitlementID ?? `active-license-${Math.random()}`,
        };
      }));
    } catch (err) {
      setActiveLicenses([]);
      setActiveLicensesError("Gabim gjatë ngarkimit të licencave aktive për SEF.");
      errorAlert("Gabim gjatë ngarkimit të licencave aktive për SEF.");
    } finally {
      setActiveLicensesLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    loadActiveLicenses();
  }, []);

  const openApproveDialog = (row) => {
    setSelectedRenewal(row);
    setApproveStartDate(toDateInputValue(row?.startDate || row?.StartDate || ""));
    setApproveEndDate(toDateInputValue(row?.endDate || row?.EndDate || ""));
    setApproveNote("");
    setApproveError("");
    setApproveDialogOpen(true);
  };

  const closeApproveDialog = () => {
    if (approving) {
      return;
    }

    setApproveDialogOpen(false);
    setSelectedRenewal(null);
    setApproveStartDate("");
    setApproveEndDate("");
    setApproveNote("");
    setApproveError("");
  };

  const openRejectDialog = (row) => {
    setSelectedRenewal(row);
    setRejectReason("");
    setRejectError("");
    setRejectDialogOpen(true);
  };

  const closeRejectDialog = () => {
    if (rejecting) {
      return;
    }

    setRejectDialogOpen(false);
    setSelectedRenewal(null);
    setRejectReason("");
    setRejectError("");
  };

  const handleApproveConfirm = async () => {
    if (!selectedRenewal) {
      return;
    }

    if (!approveStartDate || !approveEndDate) {
      setApproveError("Datat e fillimit dhe të skadimit janë të detyrueshme.");
      return;
    }

    if (approveEndDate < approveStartDate) {
      setApproveError("Data e skadimit nuk mund të jetë më e vogël se data e fillimit.");
      return;
    }

    setApproving(true);
    setApproveError("");

    try {
      await axios.post(
        ApiLink.API_URL + `License/bar-tree-renewals/${selectedRenewal.entitlementId}/approve`,
        {
          startDate: approveStartDate,
          endDate: approveEndDate,
          note: approveNote.trim() || null,
        },
        { headers }
      );

      handleSuccess("Kërkesa për rilicencim u aprovua.");
      closeApproveDialog();
      await loadData();
    } catch (err) {
      const status = Number(err?.response?.status || 0);
      const message = getApiErrorMessage(err, "Aprovimi nuk u krye.");

      if (status === 409) {
        errorAlert("Kërkesa është procesuar më parë.");
      } else {
        errorAlert(message);
      }

      setApproveError(status === 409 ? "Kërkesa është procesuar më parë." : message);
      await loadData();
    } finally {
      setApproving(false);
    }
  };

  const handleRejectConfirm = async () => {
    const note = rejectReason.trim();

    if (!selectedRenewal || !note) {
      setRejectError("Arsyeja e refuzimit është e detyrueshme.");
      return;
    }

    setRejecting(true);
    setRejectError("");

    try {
      await axios.post(
        ApiLink.API_URL + `License/bar-tree-renewals/${selectedRenewal.entitlementId}/reject`,
        { note },
        { headers }
      );

      handleSuccess("Kërkesa për rilicencim u refuzua.");
      closeRejectDialog();
      await loadData();
    } catch (err) {
      const status = Number(err?.response?.status || 0);
      const message = getApiErrorMessage(err, "Refuzimi nuk u krye.");

      if (status === 409) {
        errorAlert("Kërkesa është procesuar më parë.");
      } else {
        errorAlert(message);
      }

      setRejectError(status === 409 ? "Kërkesa është procesuar më parë." : message);
      await loadData();
    } finally {
      setRejecting(false);
    }
  };

  const filteredRows = useMemo(() => {
    const query = String(quickFilter.join(" ") || "").trim().toLowerCase();
    if (!query) {
      return rows;
    }

    return rows.filter((row) => {
      const searchable = [
        row.companyName,
        row.productCode,
        row.fiscalCashRegisterName,
        row.renewalRequestedDeviceName || row.renewalRequestedDeviceName,
        row.machineHash || row.MachineHash,
        row.fiscalCashRegisterId || row.FiscalCashRegisterId || row.FiscalCashRegisterID,
        row.renewalRequestedDeviceName,
        row.renewalRequestedAppVersion,
        row.renewalStatus,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(query);
    });
  }, [rows, quickFilter]);

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [quickFilter]);

  const pagedRows = useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    return filteredRows.slice(start, start + paginationModel.pageSize);
  }, [filteredRows, paginationModel]);

  const activeLicenseColumns = useMemo(
    () => [
      {
        field: "companyName",
        headerName: "Kompania",
        minWidth: 180,
        flex: 1.2,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "productCode",
        headerName: "ProductCode",
        minWidth: 120,
        flex: 0.8,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "fiscalCashRegisterName",
        headerName: "POS/Arka fiskale",
        minWidth: 160,
        flex: 1,
        headerClassName: "super-app-theme--header",
        renderCell: ({ row }) => row.fiscalCashRegisterName || row.FiscalCashRegisterName || "-",
      },
      {
        field: "posId",
        headerName: "ATK POS ID",
        minWidth: 150,
        flex: 0.9,
        headerClassName: "super-app-theme--header",
        renderCell: ({ row }) => {
          const posId = row.renewalRequestedDeviceName || row.renewalRequestedDeviceName || row.machineHash || row.MachineHash || row.fiscalCashRegisterId || row.FiscalCashRegisterId || row.FiscalCashRegisterID;
          return posId ? String(posId) : "-";
        },
      },
      {
        field: "startDate",
        headerName: "Fillimi i licencës",
        minWidth: 135,
        flex: 0.9,
        headerClassName: "super-app-theme--header",
        renderCell: ({ row }) => formatDate(row.startDate || row.StartDate),
      },
      {
        field: "endDate",
        headerName: "Skadimi i licencës",
        minWidth: 135,
        flex: 0.9,
        headerClassName: "super-app-theme--header",
        renderCell: ({ row }) => formatDate(row.endDate || row.EndDate),
      },
      {
        field: "isActive",
        headerName: "Statusi",
        minWidth: 120,
        flex: 0.75,
        headerClassName: "super-app-theme--header",
        renderCell: ({ row }) => {
          const isActive = row.isActive ?? row.IsActive;
          return (
            <Chip
              label={isActive ? "Aktive" : "Joaktive"}
              color={isActive ? "success" : "default"}
              size="small"
              sx={{ borderRadius: "999px", fontWeight: 700 }}
            />
          );
        },
      },
    ],
    []
  );

  const columns = useMemo(
    () => [
      {
        field: "companyName",
        headerName: "Kompania",
        minWidth: 180,
        flex: 1.2,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "productCode",
        headerName: "ProductCode",
        minWidth: 120,
        flex: 0.8,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "fiscalCashRegisterName",
        headerName: "POS/Arka fiskale",
        minWidth: 160,
        flex: 1,
        headerClassName: "super-app-theme--header",
        renderCell: ({ row }) => row.fiscalCashRegisterName || "-",
      },
      {
        field: "posId",
        headerName: "ATK POS ID",
        minWidth: 150,
        flex: 0.9,
        headerClassName: "super-app-theme--header",
        renderCell: ({ row }) => {
          const posId = row.renewalRequestedDeviceName || row.renewalRequestedDeviceName || row.machineHash || row.MachineHash || row.fiscalCashRegisterId || row.FiscalCashRegisterId || row.FiscalCashRegisterID;
          return posId ? String(posId) : "-";
        },
      },
      {
        field: "renewalRequestedDeviceName",
        headerName: "Pajisja",
        minWidth: 140,
        flex: 0.95,
        headerClassName: "super-app-theme--header",
        renderCell: ({ row }) => row.renewalRequestedDeviceName || "-",
      },
      {
        field: "renewalRequestedAppVersion",
        headerName: "Versioni i SEF",
        minWidth: 140,
        flex: 0.9,
        headerClassName: "super-app-theme--header",
        renderCell: ({ row }) => row.renewalRequestedAppVersion || "-",
      },
      {
        field: "startDate",
        headerName: "Fillimi i licencës",
        minWidth: 135,
        flex: 0.9,
        headerClassName: "super-app-theme--header",
        renderCell: ({ row }) => formatDate(row.startDate),
      },
      {
        field: "endDate",
        headerName: "Skadimi i licencës",
        minWidth: 135,
        flex: 0.9,
        headerClassName: "super-app-theme--header",
        renderCell: ({ row }) => formatDate(row.endDate),
      },
      {
        field: "renewalRequestedAt",
        headerName: "Data e kërkesës",
        minWidth: 135,
        flex: 0.9,
        headerClassName: "super-app-theme--header",
        renderCell: ({ row }) => formatDate(row.renewalRequestedAt),
      },
      {
        field: "renewalStatus",
        headerName: "Statusi",
        minWidth: 120,
        flex: 0.8,
        headerClassName: "super-app-theme--header",
        renderCell: ({ row }) => {
          const chipProps = getStatusChipProps(row.renewalStatus);
          return <Chip label={chipProps.label} color={chipProps.color} size="small" sx={chipProps.sx} />;
        },
      },
      {
        field: "actions",
        headerName: "Veprimet",
        minWidth: 170,
        flex: 0.95,
        sortable: false,
        filterable: false,
        headerClassName: "super-app-theme--header",
        renderCell: ({ row }) => (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "center", width: "100%" }}>
            <Button variant="outlined" size="small" onClick={() => openApproveDialog(row)}>
              Rilicenso
            </Button>
            <Button variant="outlined" size="small" onClick={() => openRejectDialog(row)}>
              Refuzo
            </Button>
          </Box>
        ),
      },
    ],
    [openApproveDialog, openRejectDialog]
  );

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <CrudPageHeader
          icon={MdOutlineWorkspacePremium}
          title="Kërkesat e licencimit SEF"
          description="Shiko kërkesat e rilicencimit për SEF dhe menaxho listën e tyre nga administrimi i licencave."
          tutorialUrl="https://www.youtube.com/@Accountree"
          tutorialLabel="Shiko Tutorialin"
          className="PITP__header"
          action={
            <Button
              variant="contained"
              onClick={loadData}
              sx={{
                backgroundColor: "#284945",
                color: "#fff",
                borderRadius: "10px",
                textTransform: "none",
                px: 2,
                py: 1,
                boxShadow: "none",
                "&:hover": { backgroundColor: "#1f3a36" },
              }}
            >
              Rifresko
            </Button>
          }
        />
      </div>

      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} sx={{ color: "#284945" }} />
          <Typography variant="body2" sx={{ ml: 2, color: "#475569" }}>
            Duke ngarkuar kërkesat...
          </Typography>
        </Box>
      ) : null}

      {!loading && !error && filteredRows.length === 0 ? (
        <Box
          sx={{
            border: "1px solid #d4e2db",
            borderRadius: "12px",
            background: "#f5f9f7",
            p: 3,
            color: "#475569",
          }}
        >
          Nuk ka kërkesa për rilicencim për SEF në këtë moment.
        </Box>
      ) : null}

      {!loading && error ? (
        <Box
          sx={{
            border: "1px solid #fca5a5",
            borderRadius: "12px",
            background: "#fef2f2",
            color: "#991b1b",
            p: 3,
          }}
        >
          {error}
        </Box>
      ) : null}

      {!loading && !error && filteredRows.length > 0 ? (
        <ReusableServerDataGrid
          columns={columns}
          rows={pagedRows.map((item, index) => ({
            ...item,
            id: item.entitlementId ?? `bar-tree-renewal-${paginationModel.page}-${index}`,
            rowInfo: item,
          }))}
          loading={false}
          rowCount={filteredRows.length}
          height={620}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          sortModel={sortModel}
          setSortModel={setSortModel}
          filterModel={filterModel}
          setFilterModel={setFilterModel}
          quickFilter={quickFilter}
          setQuickFilter={setQuickFilter}
          showEditAction={false}
          showDeleteAction={false}
          showViewAction={false}
          actionsMinWidth={180}
          actionsFlex={1}
        />
      ) : null}

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#1f3a36", mb: 1.5 }}>
          Licencat aktive për SEF
        </Typography>

        {activeLicensesLoading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
            <CircularProgress size={20} sx={{ color: "#284945" }} />
            <Typography variant="body2" color="text.secondary">
              Duke ngarkuar licencat aktive...
            </Typography>
          </Box>
        ) : null}

        {!activeLicensesLoading && activeLicensesError ? (
          <Box sx={{ border: "1px solid #fca5a5", borderRadius: "12px", background: "#fef2f2", color: "#991b1b", p: 3 }}>
            {activeLicensesError}
          </Box>
        ) : null}

        {!activeLicensesLoading && !activeLicensesError && activeLicenses.length > 0 ? (
          <ReusableServerDataGrid
            columns={activeLicenseColumns}
            rows={activeLicenses.map((item) => ({
              ...item,
              id: item.entitlementId ?? item.EntitlementID ?? `active-license-${Math.random()}`,
              rowInfo: item,
            }))}
            loading={false}
            rowCount={activeLicenses.length}
            height={420}
            paginationModel={{ page: 0, pageSize: 25 }}
            setPaginationModel={() => {}}
            sortModel={[]}
            setSortModel={() => {}}
            filterModel={{ items: [] }}
            setFilterModel={() => {}}
            quickFilter={[]}
            setQuickFilter={() => {}}
            showEditAction={false}
            showDeleteAction={false}
            showViewAction={false}
            actionsMinWidth={120}
            actionsFlex={0.4}
          />
        ) : null}

        {!activeLicensesLoading && !activeLicensesError && activeLicenses.length === 0 ? (
          <Box sx={{ border: "1px solid #d4e2db", borderRadius: "12px", background: "#f5f9f7", p: 3, color: "#475569" }}>
            Nuk ka licenca aktive për SEF për momentin.
          </Box>
        ) : null}
      </Box>

      <Dialog open={approveDialogOpen} onClose={closeApproveDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1f3a36" }}>
          Aprovoni kërkesën e rilicencimit
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Kompania: <strong>{selectedRenewal?.companyName || "-"}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pajisja / POS: <strong>{selectedRenewal?.fiscalCashRegisterName || selectedRenewal?.renewalRequestedDeviceName || "-"}</strong>
            </Typography>
            <TextField
              label="Data e fillimit të licencës"
              type="date"
              value={approveStartDate}
              onChange={(event) => {
                setApproveStartDate(event.target.value);
                if (approveError) {
                  setApproveError("");
                }
              }}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              error={Boolean(approveError)}
            />
            <TextField
              label="Data e skadimit të licencës"
              type="date"
              value={approveEndDate}
              onChange={(event) => {
                setApproveEndDate(event.target.value);
                if (approveError) {
                  setApproveError("");
                }
              }}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              error={Boolean(approveError)}
            />
            <TextField
              label="Shënim (opsional)"
              value={approveNote}
              onChange={(event) => setApproveNote(event.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
            {approveError ? (
              <Typography variant="body2" color="error.main">
                {approveError}
              </Typography>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeApproveDialog} disabled={approving}>
            Anulo
          </Button>
          <Button
            variant="contained"
            onClick={handleApproveConfirm}
            disabled={approving || !approveStartDate || !approveEndDate}
            sx={{
              backgroundColor: "#284945",
              color: "#fff",
              textTransform: "none",
              minWidth: 180,
              "&:hover": { backgroundColor: "#1f3a36" },
            }}
          >
            {approving ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} sx={{ color: "#fff" }} />
                Duke aprovuar...
              </Box>
            ) : (
              "Konfirmo aprovimin"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectDialogOpen} onClose={closeRejectDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1f3a36" }}>
          Refuzo kërkesën e rilicencimit
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Kompania: <strong>{selectedRenewal?.companyName || "-"}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pajisja / POS: <strong>{selectedRenewal?.fiscalCashRegisterName || selectedRenewal?.renewalRequestedDeviceName || "-"}</strong>
            </Typography>
            <TextField
              label="Arsyeja e refuzimit"
              value={rejectReason}
              onChange={(event) => {
                setRejectReason(event.target.value);
                if (rejectError) {
                  setRejectError("");
                }
              }}
              multiline
              minRows={3}
              fullWidth
              required
              error={Boolean(rejectError)}
            />
            {rejectError ? (
              <Typography variant="body2" color="error.main">
                {rejectError}
              </Typography>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeRejectDialog} disabled={rejecting}>
            Anulo
          </Button>
          <Button
            variant="contained"
            onClick={handleRejectConfirm}
            disabled={rejecting || !rejectReason.trim()}
            sx={{
              backgroundColor: "#284945",
              color: "#fff",
              textTransform: "none",
              minWidth: 180,
              "&:hover": { backgroundColor: "#1f3a36" },
            }}
          >
            {rejecting ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} sx={{ color: "#fff" }} />
                Duke refuzuar...
              </Box>
            ) : (
              "Konfirmo refuzimin"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
